import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { homeCareApi } from "../services/api";
import SheetHandle from "../components/shared/SheetHandle";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import "./HomeCarePage.css";

const TASK_LABELS = {
  maintenance: "Maintenance",
  filter_due: "Filter",
  service_due: "Service",
  service: "Service",
  seasonal: "Seasonal",
  utility_observation: "Utility signal",
  safety: "Safety",
  inspection: "Inspection",
};
const todayValue = () => new Date().toLocaleDateString("en-CA");
const money = (value) =>
  value == null
    ? ""
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
const displayDate = (value) =>
  value
    ? new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Not scheduled";
const TASK_TITLE_SUGGESTIONS = {
  maintenance: [
    "Replace HVAC filter",
    "Clean gutters",
    "Flush water heater",
    "Test smoke detectors",
    "Change furnace filter",
    "Inspect roof",
  ],
  service: [
    "Service the water heater",
    "Service the HVAC system",
    "Service the washing machine",
    "Service the dishwasher",
  ],
  inspection: [
    "Annual home inspection",
    "Roof inspection",
    "Chimney inspection",
    "Termite inspection",
  ],
  safety: [
    "Test smoke detectors",
    "Test CO detectors",
    "Check fire extinguisher pressure",
    "Inspect electrical panel",
  ],
  seasonal: [
    "Winterize outdoor faucets",
    "Service the furnace before winter",
    "Clean gutters before fall",
    "Service the AC before summer",
  ],
  filter_due: [
    "Replace HVAC filter",
    "Replace refrigerator water filter",
    "Replace humidifier filter",
  ],
};

function TaskCard({ task, onEdit, onComplete, onSnooze, onDismiss, busy }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const runAction = (action) => {
    setMenuOpen(false);
    action(task);
  };
  const due = task.due_date ? new Date(`${task.due_date}T12:00:00`) : null;
  const days = due ? Math.ceil((due - new Date()) / 86400000) : null;
  const timing =
    days == null
      ? "No date"
      : days < 0
        ? `${Math.abs(days)}d overdue`
        : days === 0
          ? "Due today"
          : `Due in ${days}d`;
  return (
    <article className={`hm-care-task is-${task.priority || "normal"}`}>
      <div className="hm-care-task__icon" aria-hidden="true">
        {task.priority === "urgent" ? "!" : "✓"}
      </div>
      <div className="hm-care-task__copy">
        <div className="hm-care-task__meta">
          <span>{TASK_LABELS[task.task_type] || "Home care"}</span>
          <span className="hm-care-task__meta-right">
            <em>{timing}</em>
            <button
              type="button"
              className="hm-care-task__menu-trigger"
              aria-label="Task actions"
              onClick={() => setMenuOpen(true)}
              disabled={busy}
            >
              ›
            </button>
          </span>
        </div>
        <strong>{task.title}</strong>
        {task.equipment_name && <small>{task.equipment_name}</small>}
        <small
          className={`hm-care-task__assignee${task.assignee_name ? " is-assigned" : ""}`}
        >
          {task.assignee_name
            ? `Assigned to ${task.assignee_name}`
            : "Unassigned"}
        </small>
        {task.details && <p>{task.details}</p>}
      </div>
      {menuOpen && (
        <div
          className="hm-care-sheet-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setMenuOpen(false);
          }}
        >
          <section
            className="hm-care-sheet hm-care-action-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={`${task.title} actions`}
          >
            <SheetHandle onClose={() => setMenuOpen(false)} label="Close actions" />
            <header>
              <div>
                <span>{TASK_LABELS[task.task_type] || "Home care"}</span>
                <h2>{task.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="hm-care-action-menu">
              <button
                type="button"
                className="hm-care-action-row is-primary"
                onClick={() => runAction(onComplete)}
                disabled={busy}
              >
                Mark done
              </button>
              <button
                type="button"
                className="hm-care-action-row"
                onClick={() => runAction(onSnooze)}
                disabled={busy}
              >
                Snooze
              </button>
              <button
                type="button"
                className="hm-care-action-row"
                onClick={() => runAction(onEdit)}
                disabled={busy}
              >
                Edit
              </button>
              {task.case_id && (
                <Link
                  to={`/app?case=${task.case_id}`}
                  className="hm-care-action-row"
                  onClick={() => setMenuOpen(false)}
                >
                  View Case ›
                </Link>
              )}
              <button
                type="button"
                className="hm-care-action-row is-danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDismiss(task.id);
                }}
                disabled={busy}
              >
                Dismiss
              </button>
            </div>
          </section>
        </div>
      )}
    </article>
  );
}

function TaskDrawer({ title, tasks, taskProps, onClose }) {
  return (
    <div className="hm-care-sheet-backdrop" role="presentation">
      <section
        className="hm-care-sheet hm-care-list-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <SheetHandle onClose={onClose} label={`Close ${title}`} />
        <header>
          <div>
            <span>Home Care</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="hm-care-list-sheet__body">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} {...taskProps} />
          ))}
        </div>
      </section>
    </div>
  );
}

function TaskSheet({ mode, task, data, onClose, onSubmit, busy }) {
  const isComplete = mode === "complete";
  const isSnooze = mode === "snooze";
  const currentMember = (data.household_members || []).find(
    (member) => member.is_current_user,
  );
  const [form, setForm] = useState(() => ({
    title: task?.title || "",
    task_type: task?.task_type || "maintenance",
    assignee_user_id: task?.assignee_user_id || currentMember?.user_id || "",
    vault_item_id: task?.vault_item_id || "",
    due_date: task?.due_date || todayValue(),
    priority: task?.priority || "normal",
    recurrence_days: task?.recurrence_days || "",
    details: task?.details || "",
    completed_on: todayValue(),
    cost: "",
    contractor_id: "",
    provider: "",
    notes: task?.details || "",
    next_service_date: "",
    days: "7",
  }));
  const set = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const title = isComplete
    ? "Complete home care"
    : isSnooze
      ? "Snooze until later"
      : task
        ? "Update home care"
        : "Schedule home care";
  return (
    <div
      className="hm-care-sheet-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="hm-care-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <SheetHandle onClose={onClose} label={`Close ${title}`} />
        <header>
          <div>
            <span>Home Care</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <datalist id="hm-care-title-suggestions">
          {(TASK_TITLE_SUGGESTIONS[form.task_type] || []).map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(form);
          }}
        >
          {isSnooze ? (
            <>
              <p className="hm-care-sheet__intro">
                Move <strong>{task.title}</strong> out of Now without dismissing
                it.
              </p>
              <label>
                Remind me in
                <select
                  value={form.days}
                  onChange={(event) => set("days", event.target.value)}
                >
                  <option value="1">Tomorrow</option>
                  <option value="7">1 week</option>
                  <option value="14">2 weeks</option>
                  <option value="30">30 days</option>
                </select>
              </label>
              <label>
                Or choose a date
                <input
                  type="date"
                  value={form.due_date || ""}
                  onChange={(event) => set("due_date", event.target.value)}
                />
              </label>
            </>
          ) : isComplete ? (
            <>
              <p className="hm-care-sheet__intro">
                This closes the task and, when equipment is linked, saves a
                permanent service record in Home Vault.
              </p>
              <div className="hm-care-form-grid">
                <label>
                  Completed on
                  <input
                    type="date"
                    required
                    value={form.completed_on}
                    onChange={(event) =>
                      set("completed_on", event.target.value)
                    }
                  />
                </label>
                <label>
                  Cost
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Optional"
                    value={form.cost}
                    onChange={(event) => set("cost", event.target.value)}
                  />
                </label>
              </div>
              <label>
                Trusted professional
                <select
                  value={form.contractor_id}
                  onChange={(event) => set("contractor_id", event.target.value)}
                >
                  <option value="">None selected</option>
                  {(data.contractors || []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                      {row.trade ? ` · ${row.trade}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Provider name
                <input
                  value={form.provider}
                  placeholder="If not in saved professionals"
                  onChange={(event) => set("provider", event.target.value)}
                />
              </label>
              <label>
                Service notes
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(event) => set("notes", event.target.value)}
                />
              </label>
              <div className="hm-care-form-grid">
                <label>
                  Repeat every
                  <select
                    value={form.recurrence_days}
                    onChange={(event) =>
                      set("recurrence_days", event.target.value)
                    }
                  >
                    <option value="">No automatic repeat</option>
                    <option value="30">30 days</option>
                    <option value="90">3 months</option>
                    <option value="180">6 months</option>
                    <option value="365">1 year</option>
                  </select>
                </label>
                <label>
                  Or next service
                  <input
                    type="date"
                    value={form.next_service_date}
                    onChange={(event) =>
                      set("next_service_date", event.target.value)
                    }
                  />
                </label>
              </div>
            </>
          ) : (
            <>
              <label>
                What needs attention?
                <input
                  required
                  maxLength="255"
                  list="hm-care-title-suggestions"
                  value={form.title}
                  placeholder="e.g. Service the water heater"
                  onChange={(event) => set("title", event.target.value)}
                />
              </label>
              <div className="hm-care-form-grid">
                <label>
                  Type
                  <select
                    value={form.task_type}
                    onChange={(event) => set("task_type", event.target.value)}
                  >
                    <option value="maintenance">Maintenance</option>
                    <option value="service">Service</option>
                    <option value="inspection">Inspection</option>
                    <option value="safety">Safety</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="filter_due">Filter</option>
                  </select>
                </label>
                <label>
                  Priority
                  <select
                    value={form.priority}
                    onChange={(event) => set("priority", event.target.value)}
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label>
              </div>
              <label>
                Equipment in Home Vault
                <select
                  value={form.vault_item_id}
                  onChange={(event) => set("vault_item_id", event.target.value)}
                >
                  <option value="">General home care</option>
                  {(data.equipment || []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                      {row.location ? ` · ${row.location}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Assigned to
                <select
                  value={form.assignee_user_id || ""}
                  onChange={(event) =>
                    set("assignee_user_id", event.target.value)
                  }
                >
                  <option value="">Unassigned household task</option>
                  {(data.household_members || []).map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.name}
                      {member.is_current_user ? " · You" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <div className="hm-care-form-grid">
                <label>
                  Due date
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(event) => set("due_date", event.target.value)}
                  />
                </label>
                <label>
                  Repeat
                  <select
                    value={form.recurrence_days}
                    onChange={(event) =>
                      set("recurrence_days", event.target.value)
                    }
                  >
                    <option value="">Does not repeat</option>
                    <option value="30">Monthly</option>
                    <option value="90">Every 3 months</option>
                    <option value="180">Every 6 months</option>
                    <option value="365">Yearly</option>
                  </select>
                </label>
              </div>
              <label>
                Notes
                <textarea
                  rows="3"
                  value={form.details}
                  placeholder="What should be checked or completed?"
                  onChange={(event) => set("details", event.target.value)}
                />
              </label>
            </>
          )}
          <footer>
            <button type="button" className="is-secondary" onClick={onClose}>
              Cancel
            </button>
            <button disabled={busy}>
              {busy
                ? "Saving…"
                : isComplete
                  ? "Complete and save"
                  : isSnooze
                    ? "Snooze task"
                    : "Save home care"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function Records({ data }) {
  const services = data.recent_service || [];
  const projects = data.projects || [];
  const pros = data.contractors || [];
  return (
    <section className="hm-care-records">
      <div className="hm-care-records__head">
        <div>
          <span>Home records</span>
          <h2>History, projects & pros</h2>
        </div>
        <Link to="/app/home-vault">Open full Vault ›</Link>
      </div>
      <div className="hm-care-records__grid">
        <details>
          <summary>
            <span>
              Service history
              <small>
                {services.length
                  ? `${services.length} recent records`
                  : "No service recorded yet"}
              </small>
            </span>
            <b>{services.length}</b>
          </summary>
          <div>
            {services.slice(0, 4).map((row) => (
              <article key={row.id}>
                <strong>{row.service_type || "Home service"}</strong>
                <span>
                  {row.item_name || row.provider || "General home care"}
                </span>
                <small>
                  {displayDate(row.service_date)}
                  {row.cost != null ? ` · ${money(row.cost)}` : ""}
                </small>
              </article>
            ))}
            {!services.length && (
              <p>Completed service records appear here.</p>
            )}
          </div>
        </details>
        <details>
          <summary>
            <span>
              Projects
              <small>
                {projects.length
                  ? "Improvements already in Home Vault"
                  : "No projects recorded yet"}
              </small>
            </span>
            <b>{projects.length}</b>
          </summary>
          <div>
            {projects.slice(0, 4).map((row) => (
              <article key={row.id}>
                <strong>{row.name}</strong>
                <span>{row.category || "Home improvement"}</span>
                <small>
                  {row.completed_date
                    ? displayDate(row.completed_date)
                    : "In planning"}
                  {row.cost != null ? ` · ${money(row.cost)}` : ""}
                </small>
              </article>
            ))}
            {!projects.length && (
              <p>
                Add major improvements in Home Vault; Home Care will keep them
                visible here.
              </p>
            )}
          </div>
        </details>
        <details>
          <summary>
            <span>
              Trusted professionals
              <small>
                {pros.length
                  ? "Saved service providers"
                  : "No professionals saved yet"}
              </small>
            </span>
            <b>{pros.length}</b>
          </summary>
          <div>
            {pros.slice(0, 4).map((row) => (
              <article key={row.id}>
                <strong>{row.name}</strong>
                <span>{row.trade || "Home professional"}</span>
                <small>
                  {row.times_used
                    ? `Used ${row.times_used} time${row.times_used === 1 ? "" : "s"}`
                    : row.phone || row.email || "Saved for later"}
                </small>
              </article>
            ))}
            {!pros.length && (
              <p>
                Professionals saved in Home Vault will be available when
                completing a task.
              </p>
            )}
          </div>
        </details>
      </div>
    </section>
  );
}

export default function HomeCarePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [taskDrawer, setTaskDrawer] = useState(null);
  const showSavingNotice = useMinLoad(busy && data !== null, 700);
  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    setError("");
    try {
      setData(
        refresh ? await homeCareApi.refresh() : await homeCareApi.snapshot(),
      );
    } catch (err) {
      setError(err.message || "Could not load Home Care");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => {
    load(false);
  }, [load]);

  const groups = useMemo(() => {
    const tasks = data?.tasks || [];
    const today = todayValue();
    const soon = new Date(Date.now() + 30 * 86400000).toLocaleDateString(
      "en-CA",
    );
    return {
      now: tasks.filter((row) => row.due_date && row.due_date <= today),
      upcoming: tasks.filter(
        (row) =>
          !row.due_date || (row.due_date > today && row.due_date <= soon),
      ),
      later: tasks.filter((row) => row.due_date && row.due_date > soon),
    };
  }, [data]);

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      setSheet(null);
      await load(false);
    } catch (err) {
      setError(err.message || "Could not update Home Care");
      setBusy(false);
    }
  };
  const dismiss = (id) => run(() => homeCareApi.dismissTask(id));
  const submitSheet = (form) => {
    const payload = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [
        key,
        value === "" ? null : value,
      ]),
    );
    if (sheet.mode === "complete")
      return run(() => homeCareApi.completeTask(sheet.task.id, payload));
    if (sheet.mode === "snooze")
      return run(() =>
        homeCareApi.snoozeTask(
          sheet.task.id,
          form.due_date && form.due_date !== sheet.task.due_date
            ? { due_date: form.due_date }
            : { days: form.days },
        ),
      );
    if (sheet.task)
      return run(() => homeCareApi.updateTask(sheet.task.id, payload));
    return run(() => homeCareApi.createTask(payload));
  };
  const summary = data?.summary || {};
  const taskProps = {
    busy,
    onEdit: (task) => setSheet({ mode: "edit", task }),
    onComplete: (task) => setSheet({ mode: "complete", task }),
    onSnooze: (task) => setSheet({ mode: "snooze", task }),
    onDismiss: dismiss,
  };
  const taskList = (tasks, label) => (
    <>
      {tasks.slice(0, 3).map((task) => (
        <TaskCard key={task.id} task={task} {...taskProps} />
      ))}
      {tasks.length > 3 && (
        <button
          type="button"
          className="hm-care-view-all"
          onClick={() => setTaskDrawer({ title: label, tasks })}
        >
          View all {tasks.length} {label.toLowerCase()} ›
        </button>
      )}
    </>
  );
  return (
    <main className="hm-care-page">
      <header className="hm-care-hero">
        <div>
          <span>Home Care</span>
          <h1>Keep your home running</h1>
          <p>Plan maintenance, track service, and stay ahead of home care.</p>
        </div>
      </header>
      <section className="hm-care-toolbar" aria-label="Home Care actions">
        <button
          type="button"
          className="is-primary"
          onClick={() => setSheet({ mode: "create", task: null })}
        >
          + Schedule care
        </button>
        <button type="button" onClick={() => load(true)} disabled={busy}>
          {busy ? "Checking…" : "Refresh"}
        </button>
      </section>
      <section className="hm-care-summary" aria-label="Home Care summary">
        <div className={summary.due_now ? "needs-care" : ""}>
          <strong>{summary.due_now || 0}</strong>
          <span>Due now</span>
        </div>
        <div>
          <strong>{summary.upcoming || 0}</strong>
          <span>Upcoming</span>
        </div>
        <div>
          <strong>{summary.active_cases || 0}</strong>
          <span>Active repairs</span>
        </div>
        <div>
          <strong>{summary.projects || 0}</strong>
          <span>Projects</span>
        </div>
      </section>
      {error && <div className="hm-care-error">{error}</div>}
      {!data && !error ? (
        <div className="hm-care-loading">Organizing your home care…</div>
      ) : (
        <>
          <section className="hm-care-section">
            <div className="hm-care-section__head">
              <span>Now</span>
              <strong>{groups.now.length}</strong>
            </div>
            {groups.now.length ? (
              taskList(groups.now, "Due now")
            ) : (
              <div className="hm-care-clear">
                <b>All calm right now</b>
                <span>No overdue home care tasks.</span>
                <button
                  type="button"
                  onClick={() => setSheet({ mode: "create", task: null })}
                >
                  Schedule home care
                </button>
              </div>
            )}
          </section>
          <section className="hm-care-section">
            <div className="hm-care-section__head">
              <span>Upcoming</span>
              <strong>{groups.upcoming.length}</strong>
            </div>
            {groups.upcoming.length ? (
              taskList(groups.upcoming, "Upcoming care")
            ) : (
              <div className="hm-care-clear">
                <b>Nothing scheduled soon</b>
                <span>
                  Add a task here or add service dates to equipment in Home
                  Vault.
                </span>
              </div>
            )}
          </section>
          {groups.later.length > 0 && (
            <details className="hm-care-later">
              <summary>
                <span>
                  <b>Later</b>
                  <small>Planned more than 30 days ahead</small>
                </span>
                <strong>{groups.later.length}</strong>
              </summary>
              <div className="hm-care-later__tasks">
                {groups.later.map((task) => (
                  <TaskCard key={task.id} task={task} {...taskProps} />
                ))}
              </div>
            </details>
          )}
          {(data.cases || []).length > 0 && (
            <section className="hm-care-cases">
              <div className="hm-care-section__head">
                <span>Repairs & Cases</span>
                <strong>{data.cases.length}</strong>
              </div>
              {data.cases.slice(0, 3).map((item) => (
                <Link key={item.id} to={`/app?case=${item.id}`}>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.priority || "Review"} · Evidence-backed repair
                    </small>
                  </div>
                  <b>Review ›</b>
                </Link>
              ))}
            </section>
          )}
          <Records data={data} />
          <section className="hm-care-links">
            <Link to="/app/home-vault">
              <span>🏡</span>
              <div>
                <strong>Equipment & proof</strong>
                <small>Full records, warranties and documents</small>
              </div>
              <b>›</b>
            </Link>
            <Link to="/app/smart-home">
              <span>⌁</span>
              <div>
                <strong>Live home signals</strong>
                <small>Why connected devices raised an observation</small>
              </div>
              <b>›</b>
            </Link>
            <Link to="/app/utilities">
              <span>⚡</span>
              <div>
                <strong>Consumption & cost</strong>
                <small>Why usage raised an efficiency finding</small>
              </div>
              <b>›</b>
            </Link>
          </section>
        </>
      )}
      <div className="fd-food-askbar hm-care-askbar">
        <input
          className="fd-food-askbar__input"
          readOnly
          placeholder="✦ Ask about home care…"
          onFocus={() =>
            window.dispatchEvent(
              new CustomEvent("hm_open_ask", {
                detail: {
                  context: {
                    module: "home_care",
                    module_label: "Home Care",
                    surface: "Home Care",
                  },
                },
              }),
            )
          }
        />
        <button
          className="fd-food-askbar__button"
          type="button"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent("hm_open_ask", {
                detail: {
                  context: {
                    module: "home_care",
                    module_label: "Home Care",
                    surface: "Home Care",
                  },
                },
              }),
            )
          }
        >
          ✦ Ask
        </button>
      </div>
      {sheet && (
        <TaskSheet
          key={`${sheet.mode}:${sheet.task?.id || "new"}`}
          mode={sheet.mode}
          task={sheet.task}
          data={data || {}}
          busy={busy}
          onClose={() => setSheet(null)}
          onSubmit={submitSheet}
        />
      )}
      {taskDrawer && (
        <TaskDrawer
          title={taskDrawer.title}
          tasks={taskDrawer.tasks}
          taskProps={{
            ...taskProps,
            onEdit: (task) => {
              setTaskDrawer(null);
              setSheet({ mode: "edit", task });
            },
            onComplete: (task) => {
              setTaskDrawer(null);
              setSheet({ mode: "complete", task });
            },
            onSnooze: (task) => {
              setTaskDrawer(null);
              setSheet({ mode: "snooze", task });
            },
          }}
          onClose={() => setTaskDrawer(null)}
        />
      )}
      {showSavingNotice && <SavingNotice message="Saving home care changes..." />}
    </main>
  );
}
